import "./GpsTrackerAsignarModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { ClienteService, buscarClientes } from "../../services/terceros/ClienteService";
import { GpsTrackerAsignarSavePayload } from "../../../shared/services/api/payload/hardware/GpsTrackerAsignarSavePayload";
import { GpsTrackerAsignarService } from "../../services/hardware/GpsTrackerAsignarService";
import { GpsTrackerResponse } from "../../../shared/response/hardware/GpsTrackerResponse";

/**
 * Modal de asignación de un GPS Tracker: toggle Oficina | Cliente + buscador de clientes
 * (input type=search, typeahead por NIT o nombre sobre la lista local de ClienteService, con `buscarClientes`).
 * POST /hardware/gps-trackers/{id}/asignar (vía GpsTrackerAsignarService).
 */
export class GpsTrackerAsignarModal extends BaseSmallModalFormComponent<
  GpsTrackerResponse,
  GpsTrackerAsignarSavePayload,
  GpsTrackerAsignarService
> {
  private _aCliente: boolean;
  private _clientes: ClienteResponse[] = [];
  private _selectedId: string | null;
  private _search!: HTMLInputElement;
  private _results!: HTMLElement;

  constructor(
    private readonly _tracker: GpsTrackerResponse,
    private readonly _onSavedCb: (item: GpsTrackerResponse) => void,
  ) {
    super(new GpsTrackerAsignarService());
    this._aCliente = _tracker.clienteId != null;
    this._selectedId = _tracker.clienteId;
  }

  protected get title(): string { return "Asignación de dispositivo"; }
  protected get saveText(): string { return "Guardar"; }
  protected get saveInitiallyDisabled(): boolean { return false; }

  protected renderBody(): string {
    const codigo = this._tracker.codigoInterno;
    return `
      <div class="gt-asig-serial">Código interno · ${codigo}</div>

      <div class="gt-toggle">
        <button type="button" class="gt-toggle-opt ${this._aCliente ? "" : "on"}" data-mode="oficina">
          <div class="gt-toggle-t">Oficina</div>
          <div class="gt-toggle-s">Queda en inventario</div>
        </button>
        <button type="button" class="gt-toggle-opt ${this._aCliente ? "on" : ""}" data-mode="cliente">
          <div class="gt-toggle-t">Cliente</div>
          <div class="gt-toggle-s">Pasa a un cliente</div>
        </button>
      </div>

      <div class="gt-panel-cliente" ${this._aCliente ? "" : "hidden"}>
        <label class="cl-form-label" for="gt-cliente-search">Cliente</label>
        <div class="gt-search-wrap">
          <input id="gt-cliente-search" class="sx-input" type="search" autocomplete="off"
                 placeholder="Escribe NIT o nombre…" />
          <div class="gt-search-results" id="gt-cliente-results" hidden></div>
        </div>
      </div>

      <div class="gt-panel-oficina" ${this._aCliente ? "hidden" : ""}>
        <div class="gt-oficina-info">El dispositivo quedará disponible en el inventario de oficina para futura asignación.</div>
      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._search  = el.querySelector<HTMLInputElement>("#gt-cliente-search")!;
    this._results = el.querySelector<HTMLElement>("#gt-cliente-results")!;

    el.querySelectorAll<HTMLButtonElement>(".gt-toggle-opt").forEach(btn => {
      btn.onclick = () => this._setMode(el, btn.dataset.mode === "cliente");
    });

    this._search.addEventListener("input", () => this._onSearch());
    this._search.addEventListener("focus", () => { if (this._search.value.trim()) this._onSearch(); });
    // Cerrar resultados al perder foco (con delay para que registre el mousedown del ítem)
    this._search.addEventListener("blur", () => setTimeout(() => this._results.hidden = true, 150));

    void this._loadClientes();
  }

  private async _loadClientes(): Promise<void> {
    try {
      this._clientes = await ClienteService.instance.get();
    } catch { this._clientes = []; }
    // Prellenar si el tracker ya está asignado a un cliente
    if (this._selectedId) {
      const cli = this._clientes.find(c => c.id === this._selectedId);
      if (cli) this._search.value = this._label(cli);
    }
    this._validate();
  }

  /**
   * Busca el cliente en la lista cargada al abrir (`buscarClientes`: nombre o NIT, todas las palabras, sin distinguir
   * mayúsculas ni tildes) y muestra hasta 8. Al teclear se invalida la selección previa hasta elegir de la lista.
   */
  private _onSearch(): void {
    this._selectedId = null;
    const q = this._search.value;
    if (!q.trim()) { this._results.hidden = true; this._validate(); return; }

    const matches = buscarClientes(this._clientes, q, 8);

    if (matches.length === 0) {
      this._results.innerHTML = `<div class="gt-search-empty">Sin coincidencias</div>`;
      this._results.hidden = false;
      this._validate();
      return;
    }

    this._results.innerHTML = matches.map(c =>
      `<div class="gt-search-item" data-id="${c.id}">
        <span class="gt-search-name">${this._esc(c.nombre)}</span>
        <span class="gt-search-nit">NIT ${this._esc(c.nit)}</span>
      </div>`,
    ).join("");
    this._results.hidden = false;

    this._results.querySelectorAll<HTMLElement>(".gt-search-item").forEach(item => {
      // mousedown (no click) para que se ejecute antes del blur del input
      item.addEventListener("mousedown", (e) => {
        e.preventDefault();
        const cli = this._clientes.find(c => c.id === item.dataset.id);
        if (cli) this._selectCliente(cli);
      });
    });
    this._validate();
  }

  private _selectCliente(c: ClienteResponse): void {
    this._selectedId = c.id;
    this._search.value = this._label(c);
    this._results.hidden = true;
    this._validate();
  }

  private _setMode(el: HTMLDivElement, aCliente: boolean): void {
    this._aCliente = aCliente;
    el.querySelectorAll<HTMLButtonElement>(".gt-toggle-opt").forEach(b =>
      b.classList.toggle("on", (b.dataset.mode === "cliente") === aCliente),
    );
    el.querySelector<HTMLElement>(".gt-panel-cliente")!.toggleAttribute("hidden", !aCliente);
    el.querySelector<HTMLElement>(".gt-panel-oficina")!.toggleAttribute("hidden", aCliente);
    this._validate();
  }

  protected buildPayload(): GpsTrackerAsignarSavePayload {
    return {
      id: this._tracker.id,
      clienteId: this._aCliente ? this._selectedId : null,
    };
  }

  protected override onSaved(item: GpsTrackerResponse): void {
    this._onSavedCb(item);
    this.dispose();
  }

  /** Si es "Cliente", requiere un cliente seleccionado; "Oficina" siempre válido. */
  private _validate(): void {
    this.setSaveDisabled(this._aCliente && !this._selectedId);
  }

  private _label(c: ClienteResponse): string {
    return `${c.nombre} · NIT ${c.nit}`;
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
